// src/components/Analytics.jsx

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { db } from '../../Firebase/firebaseConfig';
import { collection, getDocs } from 'firebase/firestore';
import { Bar, Line } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';

// Register Chart.js components
Chart.register(...registerables);

const Analytics = () => {
  // **State Variables**
  const [courses, setCourses] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [messages, setMessages] = useState([]);

  // **Loading State**
  const [dataLoaded, setDataLoaded] = useState(false);

  // **Firestore Collections**
  const coursesCollection = collection(db, 'courses');
  const blogsCollection = collection(db, 'blogs');
  const messagesCollection = collection(db, 'contactMessages');

  // **Fetch Data**
  const fetchData = useCallback(async () => {
    try {
      // Fetch Courses
      const coursesSnapshot = await getDocs(coursesCollection);
      const coursesData = coursesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCourses(coursesData);

      // Fetch Blogs
      const blogsSnapshot = await getDocs(blogsCollection);
      const blogsData = blogsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setBlogs(blogsData);

      // Fetch Messages
      const messagesSnapshot = await getDocs(messagesCollection);
      const messagesData = messagesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setMessages(messagesData);

      // Set dataLoaded to true after all data is fetched
      setDataLoaded(true);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      alert('Failed to fetch analytics data.');
    }
  }, [coursesCollection, blogsCollection, messagesCollection]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // **Analytics Data**
  const [analyticsData, setAnalyticsData] = useState({
    courses: {},
    blogs: {},
    messages: {},
  });

  // **Compute Analytics Data**
  const computeAnalytics = useCallback(() => {
    if (!dataLoaded) {
      // If data hasn't loaded yet, do nothing
      return;
    }

    try {
      // Courses per month
      const courseCounts = {};
      courses.forEach((course) => {
        const month = new Date(course.startDate).toLocaleString('default', {
          month: 'short',
          year: 'numeric',
        });
        courseCounts[month] = (courseCounts[month] || 0) + 1;
      });

      // Blogs per month
      const blogCounts = {};
      blogs.forEach((blog) => {
        const month = new Date(blog.date).toLocaleString('default', {
          month: 'short',
          year: 'numeric',
        });
        blogCounts[month] = (blogCounts[month] || 0) + 1;
      });

      // Messages per month
      const messageCounts = {};
      messages.forEach((message) => {
        const timestamp = message.timestamp
          ? message.timestamp.toDate()
          : new Date(); // Fallback to current date if timestamp is missing
        const month = timestamp.toLocaleString('default', {
          month: 'short',
          year: 'numeric',
        });
        messageCounts[month] = (messageCounts[month] || 0) + 1;
      });

      setAnalyticsData({
        courses: courseCounts,
        blogs: blogCounts,
        messages: messageCounts,
      });
    } catch (error) {
      console.error('Error computing analytics:', error);
      alert('Failed to compute analytics data.');
    }
  }, [courses, blogs, messages, dataLoaded]);

  // **Trigger computeAnalytics when data is loaded**
  useEffect(() => {
    computeAnalytics();
  }, [computeAnalytics]);

  // **Prepare Data for Charts**
  const prepareChartData = useMemo(() => {
    const monthsSet = new Set([
      ...Object.keys(analyticsData.courses),
      ...Object.keys(analyticsData.blogs),
      ...Object.keys(analyticsData.messages),
    ]);
    const months = Array.from(monthsSet).sort(
      (a, b) => new Date(a) - new Date(b)
    );

    const courseCounts = months.map((month) => analyticsData.courses[month] || 0);
    const blogCounts = months.map((month) => analyticsData.blogs[month] || 0);
    const messageCounts = months.map(
      (month) => analyticsData.messages[month] || 0
    );

    return { months, courseCounts, blogCounts, messageCounts };
  }, [analyticsData]);

  const coursesChart = {
    labels: prepareChartData.months,
    datasets: [
      {
        label: 'Courses Added',
        data: prepareChartData.courseCounts,
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
      },
    ],
  };

  const blogsChart = {
    labels: prepareChartData.months,
    datasets: [
      {
        label: 'Blogs Published',
        data: prepareChartData.blogCounts,
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
      },
    ],
  };

  const messagesChart = {
    labels: prepareChartData.months,
    datasets: [
      {
        label: 'Messages Received',
        data: prepareChartData.messageCounts,
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        fill: true,
      },
    ],
  };

  // **Rendering the Component**
  return (
    <section className="admin-section analytics-section">
      <h2>Analytics</h2>
      <div className="charts-container">
        <div className="chart-card">
          <h3>Courses Over Time</h3>
          <Bar data={coursesChart} />
        </div>
        <div className="chart-card">
          <h3>Blogs Over Time</h3>
          <Bar data={blogsChart} />
        </div>
        <div className="chart-card">
          <h3>Messages Over Time</h3>
          <Line data={messagesChart} />
        </div>
      </div>
    </section>
  );
};

export default Analytics;
